import { useState } from "react";
import "./App.css";
import axios from "axios";
import {
    Phone, Mail, MapPin, Clock, ShieldCheck, HeartHandshake, Users, Sparkles,
    HandHeart, Utensils, Pill, Brain, MessageCircle, HomeIcon, ArrowRight,
    CheckCircle2, Star, AlertCircle, ChevronRight, Award, Menu, X, ExternalLink, Quote,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* --------------------------------------------------------------
   Divine Care Connections — Single Page Home
   Sage / Cream / Warm Gold. Playfair Display + DM Sans.
--------------------------------------------------------------- */

const PHONE_LANDLINE = "0203 823 7773";
const PHONE_MOBILE = "07939 970 148";
const EMAIL = "info@divinecareconnections.co.uk";
const ADDRESS = "Unit 34, Mainyard Studio, 679/691 High Road, Leyton E10 6RA";
const CQC_REPORT_URL = "https://www.cqc.org.uk/location/1-7536843028/reports/AP7001/overall";
const CQC_PROFILE_URL = "https://www.cqc.org.uk/location/1-7536843028";
const CQC_REPORT_DATE = "13 December 2024";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_wellness-enhance-2/artifacts/0v97vw44_Divine%20Care%20Logo.png";
const GOOGLE_REVIEWS_URL = "https://www.google.com/maps?cid=15465906231080336046";
const GOOGLE_WRITE_REVIEW_URL = "https://www.google.com/search?q=Divine+Care+Connections+Ltd&ludocid=15465906231080336046#lrd=0x48761d8ef6912ff9:0xd6a1efc6790b3aae,3";
const GOOGLE_RATING = 4.8;
const GOOGLE_REVIEW_COUNT = 13;

const REVIEWS = [
    {
        name: "Micheal",
        rating: 5,
        text: "The management are top notch and staff/client satisfaction are well considered.",
    },
    {
        name: "Tarek Ahmed",
        rating: 5,
        text: "Very good care worker and their services.",
    },
    {
        name: "Apollo Mwaka",
        rating: 5,
        text: "I highly recommend them to anyone looking for excellent care services.",
    },
];

const NAV = [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "CQC Rating", href: "#cqc" },
    { label: "Reviews", href: "#reviews" },
    { label: "Areas", href: "#areas" },
    { label: "Contact", href: "#contact" },
];

/* ---------------- NAV ---------------- */
const Nav = () => {
    const [open, setOpen] = useState(false);
    return (
        <nav className="dcc-nav" data-testid="site-nav">
            <div className="dcc-container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 28px" }}>
                <a href="#top" data-testid="brand-logo"
                    style={{ display: "flex", alignItems: "center", gap: 14, textDecoration: "none", color: "var(--dcc-ink)" }}>
                    <img src={LOGO_URL}
                        alt="Divine Care Connections Ltd"
                        style={{
                            height: 56,
                            width: "auto",
                            display: "block",
                            objectFit: "contain",
                        }}
                    />
                </a>

                <div className="dcc-nav-links" style={{ display: "none", gap: 30, alignItems: "center" }}>
                    {NAV.map((n) => (
                        <a key={n.href} href={n.href} className="dcc-nav-link" data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>{n.label}</a>
                    ))}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <a href={`tel:${PHONE_LANDLINE.replace(/\s/g, "")}`} className="dcc-btn dcc-btn-primary"
                        data-testid="nav-call-btn"
                        style={{ padding: "11px 18px", fontSize: 14 }}>
                        <Phone size={15} /> {PHONE_LANDLINE}
                    </a>
                    <button onClick={() => setOpen(!open)} data-testid="nav-menu-toggle"
                        className="dcc-mobile-toggle"
                        style={{
                            display: "grid", placeItems: "center",
                            width: 42, height: 42, borderRadius: 12,
                            background: "var(--dcc-ivory)", border: "1px solid var(--dcc-line)", cursor: "pointer",
                        }}>
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="dcc-container" data-testid="mobile-menu"
                    style={{ padding: "6px 28px 22px", display: "flex", flexDirection: "column", gap: 6 }}>
                    {NAV.map((n) => (
                        <a key={n.href} href={n.href} onClick={() => setOpen(false)}
                            className="dcc-nav-link"
                            data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            style={{ padding: "12px 4px", borderBottom: "1px solid var(--dcc-line-2)" }}>
                            {n.label}
                        </a>
                    ))}
                </div>
            )}

            <style>{`
                @media (min-width: 900px) {
                    .dcc-nav-links { display: flex !important; }
                    .dcc-mobile-toggle { display: none !important; }
                }
            `}</style>
        </nav>
    );
};

/* ---------------- HERO ---------------- */
const Hero = () => (
    <section id="top" style={{ position: "relative", overflow: "hidden", paddingTop: 60, paddingBottom: 100 }}>
        <div aria-hidden style={{
            position: "absolute", top: -180, right: -160, width: 520, height: 520, borderRadius: "50%",
            background: "radial-gradient(closest-side, rgba(107,142,127,0.28), transparent 70%)", filter: "blur(10px)", zIndex: 0,
        }} />
        <div aria-hidden style={{
            position: "absolute", bottom: -220, left: -100, width: 460, height: 460, borderRadius: "50%",
            background: "radial-gradient(closest-side, rgba(184,147,60,0.18), transparent 70%)", filter: "blur(10px)", zIndex: 0,
        }} />

        <div className="dcc-container" style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 60, alignItems: "center" }}>
            <div className="dcc-fade" data-testid="hero-copy">
                <div className="dcc-serif" data-testid="hero-brand-wordmark" style={{
                    fontSize: "clamp(30px, 3.6vw, 48px)",
                    fontWeight: 700,
                    letterSpacing: "-0.015em",
                    lineHeight: 1.05,
                    color: "var(--dcc-sage-900)",
                    marginBottom: 22,
                    display: "inline-flex",
                    alignItems: "baseline",
                    gap: 14,
                    flexWrap: "wrap",
                }}>
                    Divine Care Connections
                    <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        fontFamily: "var(--font-sans)",
                        fontSize: "clamp(11px, 1vw, 13px)",
                        fontWeight: 500,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: "var(--dcc-cream)",
                        background: "var(--dcc-gold)",
                        padding: "5px 11px",
                        borderRadius: 999,
                        transform: "translateY(-4px)",
                    }}>Ltd</span>
                </div>

                <span className="dcc-eyebrow">CQC-Registered · Rated Good</span>

                <h1 className="dcc-serif" style={{
                    fontSize: "clamp(40px, 5.6vw, 68px)",
                    lineHeight: 1.02,
                    marginTop: 22,
                    marginBottom: 24,
                    color: "var(--dcc-ink)",
                    letterSpacing: "-0.02em",
                }}>
                    Care that feels like <em style={{ fontStyle: "italic", color: "var(--dcc-sage-800)" }}>family</em>,
                    <br /> delivered in the comfort of home.
                </h1>

                <p style={{ fontSize: 18, lineHeight: 1.65, color: "var(--dcc-ink-soft)", maxWidth: 560, marginBottom: 34 }}>
                    Person-centred home care across London, Essex and the South East.
                    Trained, vetted carers supporting older adults, people living with dementia and those recovering
                    at home — with dignity, continuity and warmth.
                </p>

                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 34 }}>
                    <a href="#contact" className="dcc-btn dcc-btn-primary" data-testid="hero-cta-assessment">
                        Request a free care assessment <ArrowRight size={16} />
                    </a>
                    <a href={`tel:${PHONE_LANDLINE.replace(/\s/g, "")}`} className="dcc-btn dcc-btn-ghost" data-testid="hero-cta-call">
                        <Phone size={15} /> Speak to a care advisor
                    </a>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 18, color: "var(--dcc-muted)", fontSize: 13.5 }}>
                    <div style={{ display: "flex", gap: 3 }}>
                        {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={15} fill="var(--dcc-gold)" color="var(--dcc-gold)" />)}
                    </div>
                    <span>Trusted by 200+ families across the region</span>
                </div>
            </div>

            <div className="dcc-fade-2" style={{ position: "relative", display: "flex", flexDirection: "column", gap: 18 }} data-testid="hero-visual">

                {/* Trust Card 1 — CQC Good rating */}
                <div className="dcc-cqc-badge" data-testid="hero-cqc-badge" style={{ padding: 28 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                        <div style={{
                            width: 54, height: 54, borderRadius: 12,
                            background: "var(--dcc-sage-800)", color: "var(--dcc-gold-soft)",
                            display: "grid", placeItems: "center",
                        }}>
                            <Award size={26} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--dcc-sage-700)" }}>
                                CQC Overall Rating
                            </div>
                            <div className="dcc-serif" style={{ fontSize: 30, color: "var(--dcc-ink)", lineHeight: 1 }}>
                                Good
                            </div>
                        </div>
                        <div style={{
                            fontSize: 10.5, color: "var(--dcc-muted)",
                            textAlign: "right", lineHeight: 1.4,
                        }}>
                            Report<br /> {CQC_REPORT_DATE}
                        </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 5, marginBottom: 14 }}>
                        {["Safe", "Effective", "Caring", "Responsive", "Well-Led"].map((d) => (
                            <div key={d} style={{
                                background: "var(--dcc-sage-50)",
                                border: "1px solid var(--dcc-sage-100)",
                                borderRadius: 8, padding: "10px 4px",
                                textAlign: "center",
                                minWidth: 0,
                            }}>
                                <div style={{ fontSize: 9, color: "var(--dcc-sage-700)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, lineHeight: 1.1 }}>
                                    {d}
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--dcc-sage-900)" }}>Good</div>
                            </div>
                        ))}
                    </div>
                    <a href={CQC_REPORT_URL} target="_blank" rel="noopener noreferrer"
                        data-testid="hero-cqc-report-link"
                        style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            fontSize: 12.5, color: "var(--dcc-sage-800)",
                            textDecoration: "none", fontWeight: 500,
                            borderBottom: "1px solid rgba(107,142,127,0.4)", paddingBottom: 1,
                        }}>
                        Read the full CQC report <ChevronRight size={13} />
                    </a>
                </div>

                {/* Trust Card 2 — Google Reviews */}
                <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer"
                    data-testid="hero-google-card"
                    style={{
                        display: "block", textDecoration: "none",
                        background: "var(--dcc-ivory)",
                        border: "1px solid var(--dcc-line)",
                        borderRadius: 18, padding: 22,
                        transition: "transform 240ms ease, box-shadow 240ms ease, border-color 240ms ease",
                        boxShadow: "0 18px 40px -22px rgba(31,42,36,0.25)",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 24px 55px -22px rgba(31,42,36,0.32)";
                        e.currentTarget.style.borderColor = "var(--dcc-sage-300)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 18px 40px -22px rgba(31,42,36,0.25)";
                        e.currentTarget.style.borderColor = "var(--dcc-line)";
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <div style={{
                            width: 50, height: 50, borderRadius: 12,
                            background: "#fff", border: "1px solid var(--dcc-line)",
                            display: "grid", placeItems: "center", flexShrink: 0,
                        }}>
                            <GoogleG size={22} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 3 }}>
                                <div className="dcc-serif" style={{ fontSize: 26, lineHeight: 1, color: "var(--dcc-ink)" }}>
                                    {GOOGLE_RATING.toFixed(1)}
                                </div>
                                <StarRow rating={5} size={14} />
                            </div>
                            <div style={{ fontSize: 12.5, color: "var(--dcc-muted)" }}>
                                <strong style={{ color: "var(--dcc-ink)", fontWeight: 500 }}>{GOOGLE_REVIEW_COUNT} verified Google reviews</strong>
                            </div>
                        </div>
                        <ExternalLink size={16} color="var(--dcc-sage-700)" />
                    </div>
                </a>

                {/* Trust Card 3 — 24/7 Care Line */}
                <a href={`tel:${PHONE_MOBILE.replace(/\s/g, "")}`}
                    data-testid="hero-care-line-card"
                    style={{
                        display: "flex", alignItems: "center", gap: 16,
                        background: "linear-gradient(160deg, var(--dcc-sage-800) 0%, var(--dcc-sage-900) 100%)",
                        borderRadius: 18, padding: "20px 22px",
                        textDecoration: "none",
                        boxShadow: "0 20px 45px -22px rgba(47, 74, 62, 0.55)",
                        border: "1px solid rgba(184, 147, 60, 0.25)",
                        transition: "transform 240ms ease",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                    onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}>
                    <div style={{
                        width: 50, height: 50, borderRadius: 12,
                        background: "var(--dcc-gold)", color: "var(--dcc-sage-900)",
                        display: "grid", placeItems: "center", flexShrink: 0,
                    }}>
                        <Phone size={22} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--dcc-gold-soft)", marginBottom: 3 }}>
                            24/7 Care Line
                        </div>
                        <div className="dcc-serif" style={{ fontSize: 22, color: "var(--dcc-cream)", lineHeight: 1.1 }}>
                            {PHONE_MOBILE}
                        </div>
                        <div style={{ fontSize: 12, color: "rgba(246,241,231,0.7)", marginTop: 4 }}>
                            Hospital discharge or urgent care? Talk to us anytime.
                        </div>
                    </div>
                </a>
            </div>
        </div>

        <style>{`
            @media (max-width: 900px) {
                #top .dcc-container { grid-template-columns: 1fr !important; gap: 40px !important; }
            }
        `}</style>
    </section>
);

/* ---------------- TRUST BAR ---------------- */
const TrustBar = () => {
    const items = [
        { icon: ShieldCheck, label: "CQC Registered" },
        { icon: Award, label: "Care Certificate Trained" },
        { icon: HeartHandshake, label: "Person-Centred" },
        { icon: Clock, label: "24/7 Available" },
    ];
    return (
        <section data-testid="trust-bar" style={{
            background: "var(--dcc-sage-900)", color: "var(--dcc-cream)",
            padding: "22px 0",
            position: "relative", overflow: "hidden",
        }}>
            <div className="dcc-container" style={{
                display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, alignItems: "center",
            }}>
                {items.map((it) => (
                    <div key={it.label} style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "center" }}
                        data-testid={`trust-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        <it.icon size={18} color="var(--dcc-gold-2)" />
                        <span style={{ fontSize: 14, letterSpacing: "0.02em" }}>{it.label}</span>
                    </div>
                ))}
            </div>
            <style>{`@media (max-width: 720px) {
                [data-testid="trust-bar"] .dcc-container { grid-template-columns: repeat(2, 1fr) !important; gap: 14px !important; }
            }`}</style>
        </section>
    );
};

/* ---------------- ABOUT ---------------- */
const About = () => {
    const values = [
        { icon: HeartHandshake, title: "Compassion", desc: "We meet every person with kindness — because care begins with feeling truly seen." },
        { icon: Users, title: "Dignity", desc: "Every routine, wash and mealtime is delivered with respect and quiet grace." },
        { icon: Sparkles, title: "Independence", desc: "We support what you can do — never taking away what you love to do yourself." },
        { icon: Award, title: "Excellence", desc: "Trained, supervised and continuously developed carers you can rely on." },
    ];
    return (
        <section id="about" className="dcc-section" style={{ background: "var(--dcc-cream)" }} data-testid="about-section">
            <div className="dcc-container" style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 80, alignItems: "start" }}>
                <div>
                    <span className="dcc-eyebrow">About Divine Care Connections</span>
                    <h2 className="dcc-serif" style={{
                        fontSize: "clamp(32px, 3.6vw, 46px)", lineHeight: 1.1,
                        marginTop: 18, marginBottom: 22, letterSpacing: "-0.015em",
                    }}>
                        A steady, gentle presence — through every stage of life at home.
                    </h2>
                    <p style={{ fontSize: 16.5, color: "var(--dcc-ink-soft)", lineHeight: 1.75, marginBottom: 22 }}>
                        Founded by a leader with over 20 years’ experience in adult social care — spanning
                        local authority and housing association environments — Divine Care Connections was
                        built on a simple belief: people flourish best in their own home, surrounded by
                        what they love. We match each client with a small, familiar team of carers so
                        trust can grow naturally — visit by visit.
                    </p>
                    <p style={{ fontSize: 16.5, color: "var(--dcc-ink-soft)", lineHeight: 1.75, marginBottom: 30 }}>
                        From an hour a week for companionship, to complex daily support for dementia, palliative
                        or post-hospital recovery — we design care around the person, never the schedule.
                    </p>

                    <div style={{
                        borderLeft: "3px solid var(--dcc-gold)",
                        paddingLeft: 20,
                        fontFamily: "var(--font-serif)",
                        fontStyle: "italic",
                        fontSize: 19,
                        lineHeight: 1.55,
                        color: "var(--dcc-sage-900)",
                    }} data-testid="about-quote">
                        “We don’t just deliver tasks — we make small, kind moments possible. A cup of tea
                        remembered. A walk down the garden path. That is care.”
                        <div style={{ marginTop: 14, fontStyle: "normal", fontFamily: "var(--font-sans)", fontSize: 13, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--dcc-sage-700)" }}>
                            — Founder & Registered Manager
                        </div>
                    </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }} data-testid="about-values">
                    {values.map((v, i) => (
                        <div key={v.title} className="dcc-card" data-testid={`value-${v.title.toLowerCase()}`}
                            style={{ animationDelay: `${i * 80}ms` }}>
                            <div style={{
                                width: 46, height: 46, borderRadius: 12,
                                background: "var(--dcc-sage-50)", color: "var(--dcc-sage-800)",
                                display: "grid", placeItems: "center", marginBottom: 16,
                            }}>
                                <v.icon size={22} />
                            </div>
                            <h3 className="dcc-serif" style={{ fontSize: 22, marginBottom: 8, color: "var(--dcc-ink)" }}>{v.title}</h3>
                            <p style={{ fontSize: 14.5, lineHeight: 1.65, color: "var(--dcc-ink-soft)" }}>{v.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
            <style>{`@media (max-width: 900px) {
                #about .dcc-container { grid-template-columns: 1fr !important; gap: 44px !important; }
            }`}</style>
        </section>
    );
};

/* ---------------- SERVICES ---------------- */
const Services = () => {
    const services = [
        {
            icon: HandHeart, title: "Personal Care",
            desc: "Sensitive support with the daily essentials, delivered with dignity.",
            bullets: ["Washing, bathing & grooming", "Toileting & continence support", "Dressing & mobility assistance"],
        },
        {
            icon: Pill, title: "Medication Support",
            desc: "Safe prompting and administration by trained carers.",
            bullets: ["Prompting & timed reminders", "MAR chart recording", "Liaison with GP & pharmacy"],
        },
        {
            icon: Utensils, title: "Meal & Home Support",
            desc: "Nourishing meals and a calm, cared-for home environment.",
            bullets: ["Nutritious meal preparation", "Light housekeeping & laundry", "Shopping & errands"],
        },
        {
            icon: Brain, title: "Dementia Care",
            desc: "Specialist, familiar-face support that reduces distress.",
            bullets: ["Consistent named carers", "Life-story approach", "Family & carer wellbeing check-ins"],
        },
        {
            icon: MessageCircle, title: "Companionship",
            desc: "Meaningful company for good days and difficult ones.",
            bullets: ["Conversation, hobbies & walks", "Escort to appointments", "Reducing isolation & loneliness"],
        },
        {
            icon: HomeIcon, title: "Respite Care",
            desc: "Trusted cover so family carers can rest, recover, or travel.",
            bullets: ["Planned respite blocks", "Emergency short-notice cover", "Hospital-to-home transitions"],
        },
    ];
    return (
        <section id="services" className="dcc-section" style={{
            background: "linear-gradient(180deg, var(--dcc-ivory) 0%, var(--dcc-cream) 100%)",
        }} data-testid="services-section">
            <div className="dcc-container">
                <div style={{ maxWidth: 720, marginBottom: 56 }}>
                    <span className="dcc-eyebrow">Our Services</span>
                    <h2 className="dcc-serif" style={{ fontSize: "clamp(32px, 3.6vw, 46px)", marginTop: 18, marginBottom: 18, letterSpacing: "-0.015em" }}>
                        Care shaped around one person at a time.
                    </h2>
                    <p style={{ color: "var(--dcc-ink-soft)", fontSize: 17, lineHeight: 1.7 }}>
                        From a friendly hour of companionship to full daily support, our services flex with life.
                        Every plan is written with you, your family and — where appropriate — your GP or district nurse.
                    </p>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 22 }}>
                    {services.map((s, i) => (
                        <div key={s.title} className="dcc-card" data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                            style={{ animationDelay: `${i * 60}ms`, display: "flex", flexDirection: "column" }}>
                            <div style={{
                                width: 50, height: 50, borderRadius: 14,
                                background: "linear-gradient(160deg, var(--dcc-sage-700), var(--dcc-sage-900))",
                                color: "var(--dcc-gold-soft)",
                                display: "grid", placeItems: "center", marginBottom: 18,
                            }}>
                                <s.icon size={22} />
                            </div>
                            <h3 className="dcc-serif" style={{ fontSize: 23, marginBottom: 10 }}>{s.title}</h3>
                            <p style={{ fontSize: 14.5, color: "var(--dcc-ink-soft)", lineHeight: 1.6, marginBottom: 16 }}>{s.desc}</p>
                            <ul style={{ listStyle: "none", padding: 0, margin: 0, marginTop: "auto" }}>
                                {s.bullets.map((b) => (
                                    <li key={b} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14, color: "var(--dcc-ink)", marginBottom: 8 }}>
                                        <CheckCircle2 size={16} color="var(--dcc-sage-700)" style={{ marginTop: 2, flexShrink: 0 }} />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
            <style>{`
                @media (max-width: 960px) { #services .dcc-container > div:last-child { grid-template-columns: repeat(2, 1fr) !important; } }
                @media (max-width: 620px) { #services .dcc-container > div:last-child { grid-template-columns: 1fr !important; } }
            `}</style>
        </section>
    );
};

/* ---------------- CQC SECTION ---------------- */
const CQC = () => {
    const domains = [
        { label: "Safe", desc: "People are protected from abuse and avoidable harm. Risks are managed sensitively and appropriately." },
        { label: "Effective", desc: "Care is based on best practice — staff have the training, skills and supervision they need." },
        { label: "Caring", desc: "Staff involve and treat people with compassion, kindness, dignity and respect." },
        { label: "Responsive", desc: "Services meet people's needs and adapt when circumstances or preferences change." },
        { label: "Well-Led", desc: "Leadership, governance and culture support delivery of high-quality, person-centred care." },
    ];
    return (
        <section id="cqc" className="dcc-section" style={{
            background: "var(--dcc-sage-900)", color: "var(--dcc-cream)", position: "relative", overflow: "hidden",
        }} data-testid="cqc-section">
            <div aria-hidden style={{
                position: "absolute", top: -140, right: -140, width: 420, height: 420, borderRadius: "50%",
                background: "radial-gradient(closest-side, rgba(184,147,60,0.20), transparent 70%)",
            }} />
            <div className="dcc-container" style={{ position: "relative" }}>
                <div style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 60, alignItems: "start", marginBottom: 50 }}>
                    <div>
                        <span className="dcc-eyebrow" style={{ color: "var(--dcc-gold-soft)" }}>Quality & Regulation</span>
                        <h2 className="dcc-serif" style={{ fontSize: "clamp(32px, 3.6vw, 46px)", marginTop: 18, marginBottom: 20, letterSpacing: "-0.015em", color: "var(--dcc-cream)" }}>
                            Rated <em style={{ color: "var(--dcc-gold-2)", fontStyle: "italic" }}>Good</em> by the Care Quality Commission.
                        </h2>
                        <p style={{ color: "rgba(246,241,231,0.82)", fontSize: 16.5, lineHeight: 1.7 }}>
                            The CQC is England’s independent regulator of health and social care. Their five domains
                            reflect what matters most to the people we serve — and we’re proud to be rated Good across
                            every one.
                        </p>
                    </div>

                    <div style={{
                        background: "linear-gradient(160deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
                        border: "1px solid rgba(255,255,255,0.12)", borderRadius: 20, padding: 32,
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
                            <div style={{
                                width: 60, height: 60, borderRadius: 14, background: "var(--dcc-gold)",
                                color: "var(--dcc-sage-900)", display: "grid", placeItems: "center",
                            }}>
                                <Award size={28} />
                            </div>
                            <div>
                                <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--dcc-gold-soft)" }}>Overall rating</div>
                                <div className="dcc-serif" style={{ fontSize: 34, color: "var(--dcc-cream)", lineHeight: 1 }}>Good</div>
                            </div>
                        </div>
                        <p style={{ color: "rgba(246,241,231,0.78)", fontSize: 14.5, lineHeight: 1.65, marginBottom: 16 }}>
                            Assessment report published <strong style={{ color: "var(--dcc-cream)" }}>{CQC_REPORT_DATE}</strong>.
                            Independently inspected against the same standards as hospitals and care homes — because home
                            care deserves the same rigour.
                        </p>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <a href={CQC_REPORT_URL} target="_blank" rel="noopener noreferrer"
                                data-testid="cqc-report-link"
                                className="dcc-btn dcc-btn-gold" style={{ padding: "10px 16px", fontSize: 13.5 }}>
                                Read the full CQC report <ChevronRight size={14} />
                            </a>
                            <a href={CQC_PROFILE_URL} target="_blank" rel="noopener noreferrer"
                                data-testid="cqc-profile-link"
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                    padding: "10px 16px", borderRadius: 999,
                                    border: "1px solid rgba(255,255,255,0.25)",
                                    color: "var(--dcc-cream)", textDecoration: "none",
                                    fontSize: 13.5,
                                }}>
                                CQC profile page
                            </a>
                        </div>
                    </div>
                </div>

                {/* Genuine CQC assessment quote */}
                <div data-testid="cqc-quote" style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(184,147,60,0.35)",
                    borderRadius: 18,
                    padding: "28px 32px",
                    marginBottom: 36,
                    display: "flex",
                    gap: 24,
                    alignItems: "flex-start",
                }}>
                    <div style={{
                        fontFamily: "var(--font-serif)",
                        fontSize: 60, lineHeight: 0.6,
                        color: "var(--dcc-gold-2)",
                        marginTop: 8,
                    }}>“</div>
                    <div style={{ flex: 1 }}>
                        <p className="dcc-serif" style={{
                            fontStyle: "italic", fontSize: 20, lineHeight: 1.5,
                            color: "var(--dcc-cream)", marginBottom: 12,
                        }}>
                            People were pleased with the quality of the service provided. They had built trusting
                            relationships with all care staff and the registered manager. People felt safe with their
                            carer and received support to maintain their health within their own homes.
                        </p>
                        <div style={{
                            fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase",
                            color: "var(--dcc-gold-soft)",
                        }}>
                            — CQC Assessment Report · {CQC_REPORT_DATE}
                        </div>
                    </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14 }} data-testid="cqc-domains">
                    {domains.map((d) => (
                        <div key={d.label} data-testid={`cqc-domain-${d.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            style={{
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.12)",
                                borderRadius: 16, padding: 20,
                                transition: "background 220ms ease, transform 220ms ease",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.09)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.transform = "translateY(0)"; }}
                        >
                            <div style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                background: "var(--dcc-gold)", color: "var(--dcc-sage-900)",
                                padding: "3px 10px", borderRadius: 999, fontSize: 10.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
                                marginBottom: 12,
                            }}>
                                <CheckCircle2 size={12} /> Good
                            </div>
                            <h4 className="dcc-serif" style={{ fontSize: 20, color: "var(--dcc-cream)", marginBottom: 8 }}>{d.label}</h4>
                            <p style={{ fontSize: 13, lineHeight: 1.55, color: "rgba(246,241,231,0.72)" }}>{d.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
            <style>{`
                @media (max-width: 960px) { #cqc .dcc-container > div:first-child { grid-template-columns: 1fr !important; gap: 32px !important; } }
                @media (max-width: 900px) { #cqc [data-testid="cqc-domains"] { grid-template-columns: repeat(2, 1fr) !important; } }
                @media (max-width: 500px) { #cqc [data-testid="cqc-domains"] { grid-template-columns: 1fr !important; } }
            `}</style>
        </section>
    );
};

/* ---------------- WHY US ---------------- */
const WhyUs = () => {
    const pillars = [
        { icon: ShieldCheck, title: "Trained & Vetted", desc: "Every carer is Enhanced DBS checked, reference-verified and trained to the Care Certificate standard before their first visit." },
        { icon: HeartHandshake, title: "Person-Centred", desc: "Care plans are co-written with you and family — reviewed regularly so support evolves as needs do." },
        { icon: Users, title: "Continuity of Carers", desc: "We keep the same small team visiting you. Familiar faces build trust, safety and better outcomes." },
        { icon: Clock, title: "Responsive & Reachable", desc: "A real person answers the phone. Care starts within days, and out-of-hours support is always available." },
    ];
    return (
        <section id="why" className="dcc-section" style={{ background: "var(--dcc-cream)" }} data-testid="why-us-section">
            <div className="dcc-container">
                <div style={{ maxWidth: 720, marginBottom: 50 }}>
                    <span className="dcc-eyebrow">Why families choose us</span>
                    <h2 className="dcc-serif" style={{ fontSize: "clamp(32px, 3.6vw, 46px)", marginTop: 18, marginBottom: 18, letterSpacing: "-0.015em" }}>
                        Four things we refuse to compromise on.
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 22 }}>
                    {pillars.map((p, i) => (
                        <div key={p.title} className="dcc-card" data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                            style={{ animationDelay: `${i * 70}ms`, background: i % 2 === 0 ? "var(--dcc-ivory)" : "var(--dcc-sage-50)" }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 12,
                                background: "var(--dcc-sage-900)", color: "var(--dcc-gold-soft)",
                                display: "grid", placeItems: "center", marginBottom: 18,
                            }}>
                                <p.icon size={22} />
                            </div>
                            <h3 className="dcc-serif" style={{ fontSize: 21, marginBottom: 10 }}>{p.title}</h3>
                            <p style={{ fontSize: 14.5, lineHeight: 1.65, color: "var(--dcc-ink-soft)" }}>{p.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
            <style>{`
                @media (max-width: 1000px) { #why .dcc-container > div:last-child { grid-template-columns: repeat(2, 1fr) !important; } }
                @media (max-width: 560px)  { #why .dcc-container > div:last-child { grid-template-columns: 1fr !important; } }
            `}</style>
        </section>
    );
};

/* ---------------- GOOGLE REVIEWS ---------------- */
const GoogleG = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
);

const StarRow = ({ rating = 5, size = 15, color = "var(--dcc-gold)" }) => (
    <div style={{ display: "inline-flex", gap: 3 }} aria-label={`${rating} out of 5 stars`}>
        {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} size={size}
                fill={i < rating ? color : "transparent"}
                color={color}
                strokeWidth={i < rating ? 0 : 1.5}
            />
        ))}
    </div>
);

const Reviews = () => (
    <section id="reviews" className="dcc-section" style={{
        background: "linear-gradient(180deg, var(--dcc-cream) 0%, var(--dcc-ivory) 100%)",
    }} data-testid="reviews-section">
        <div className="dcc-container">
            <div style={{
                display: "grid", gridTemplateColumns: "0.85fr 1.15fr", gap: 60, alignItems: "center", marginBottom: 52,
            }}>
                <div>
                    <span className="dcc-eyebrow">What families say</span>
                    <h2 className="dcc-serif" style={{
                        fontSize: "clamp(32px, 3.6vw, 46px)",
                        marginTop: 18, marginBottom: 18, letterSpacing: "-0.015em",
                    }}>
                        Trusted by the people we serve.
                    </h2>
                    <p style={{ color: "var(--dcc-ink-soft)", fontSize: 16.5, lineHeight: 1.7 }}>
                        Every review below is verified on Google. We’re grateful for each family who has
                        taken the time to share their experience.
                    </p>
                </div>

                {/* Google rating summary card */}
                <div data-testid="google-rating-card" style={{
                    background: "var(--dcc-ivory)",
                    border: "1px solid var(--dcc-line)",
                    borderRadius: 22, padding: 30,
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    gap: 26, alignItems: "center",
                    boxShadow: "0 24px 60px -32px rgba(31,42,36,0.22)",
                }}>
                    <div style={{
                        width: 64, height: 64, borderRadius: 16,
                        background: "#fff", border: "1px solid var(--dcc-line)",
                        display: "grid", placeItems: "center",
                    }}>
                        <GoogleG size={30} />
                    </div>
                    <div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
                            <div className="dcc-serif" style={{ fontSize: 40, lineHeight: 1, color: "var(--dcc-ink)" }}>
                                {GOOGLE_RATING.toFixed(1)}
                            </div>
                            <StarRow rating={5} size={18} />
                        </div>
                        <div style={{ fontSize: 13.5, color: "var(--dcc-muted)" }}>
                            Based on <strong style={{ color: "var(--dcc-ink)" }}>{GOOGLE_REVIEW_COUNT} verified Google reviews</strong>
                        </div>
                    </div>
                    <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer"
                        data-testid="google-reviews-link"
                        className="dcc-btn dcc-btn-ghost"
                        style={{ padding: "10px 16px", fontSize: 13.5, whiteSpace: "nowrap" }}>
                        See all on Google <ExternalLink size={13} />
                    </a>
                </div>
            </div>

            {/* Review cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 22 }} data-testid="reviews-grid">
                {REVIEWS.map((r, i) => (
                    <div key={r.name} className="dcc-card" data-testid={`review-${i}`}
                        style={{
                            display: "flex", flexDirection: "column",
                            animationDelay: `${i * 80}ms`,
                            position: "relative",
                        }}>
                        <Quote size={28} color="var(--dcc-gold)"
                            style={{ position: "absolute", top: 22, right: 22, opacity: 0.4 }} />
                        <StarRow rating={r.rating} size={16} />
                        <p style={{
                            fontFamily: "var(--font-serif)",
                            fontSize: 18.5, lineHeight: 1.55, marginTop: 16, marginBottom: 22,
                            color: "var(--dcc-ink)", flex: 1,
                        }}>
                            “{r.text}”
                        </p>
                        <div style={{
                            display: "flex", alignItems: "center", gap: 12,
                            paddingTop: 18, borderTop: "1px solid var(--dcc-line-2)",
                        }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: "50%",
                                background: "linear-gradient(160deg, var(--dcc-sage-600), var(--dcc-sage-800))",
                                color: "var(--dcc-gold-soft)",
                                display: "grid", placeItems: "center",
                                fontFamily: "var(--font-serif)", fontSize: 17, fontWeight: 600,
                            }}>
                                {r.name.charAt(0)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14.5, fontWeight: 500, color: "var(--dcc-ink)" }}>{r.name}</div>
                                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--dcc-muted)" }}>
                                    <GoogleG size={11} /> Verified Google review
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Bottom row — invite a review */}
            <div style={{
                marginTop: 44,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: "var(--dcc-sage-50)",
                border: "1px solid var(--dcc-sage-100)",
                borderRadius: 18, padding: "22px 28px",
                gap: 20, flexWrap: "wrap",
            }} data-testid="reviews-invite">
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <GoogleG size={22} />
                    <div>
                        <div className="dcc-serif" style={{ fontSize: 20, color: "var(--dcc-ink)" }}>Cared for by us?</div>
                        <div style={{ fontSize: 13.5, color: "var(--dcc-ink-soft)" }}>
                            Sharing your experience on Google helps another family find good care.
                        </div>
                    </div>
                </div>
                <a href={GOOGLE_WRITE_REVIEW_URL} target="_blank" rel="noopener noreferrer"
                    data-testid="write-review-btn"
                    className="dcc-btn dcc-btn-gold"
                    style={{ fontSize: 14 }}>
                    Leave a Google review <ExternalLink size={14} />
                </a>
            </div>
        </div>
        <style>{`
            @media (max-width: 960px) {
                #reviews .dcc-container > div:first-child { grid-template-columns: 1fr !important; gap: 30px !important; }
                #reviews [data-testid="google-rating-card"] { grid-template-columns: auto 1fr !important; }
                #reviews [data-testid="google-rating-card"] a { grid-column: 1 / -1; justify-self: start; margin-top: 4px; }
            }
            @media (max-width: 900px) { #reviews [data-testid="reviews-grid"] { grid-template-columns: 1fr !important; } }
        `}</style>
    </section>
);

const Areas = () => {
    const boroughs = [
        "Waltham Forest", "Redbridge", "Newham", "Hackney", "Tower Hamlets",
        "Havering", "Barking & Dagenham", "Enfield", "Haringey", "Islington",
        "Essex — Epping Forest", "Essex — Brentwood", "Essex — Chelmsford", "Essex — Basildon",
        "Kent — Dartford", "Kent — Sevenoaks", "Surrey — Reigate", "Hertfordshire",
    ];
    return (
        <section id="areas" className="dcc-section" style={{
            background: "linear-gradient(180deg, var(--dcc-ivory) 0%, var(--dcc-cream) 100%)",
        }} data-testid="areas-section">
            <div className="dcc-container" style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 60, alignItems: "center" }}>
                <div>
                    <span className="dcc-eyebrow">Where we care</span>
                    <h2 className="dcc-serif" style={{ fontSize: "clamp(32px, 3.6vw, 46px)", marginTop: 18, marginBottom: 18, letterSpacing: "-0.015em" }}>
                        Serving London, Essex &amp; the South East.
                    </h2>
                    <p style={{ color: "var(--dcc-ink-soft)", fontSize: 16.5, lineHeight: 1.7, marginBottom: 24 }}>
                        Our head office is in Leyton, East London, with carer teams operating across multiple London
                        boroughs and out through Essex, Kent, Surrey and Hertfordshire. If you’re unsure whether we
                        cover your postcode, please call — we’ll tell you honestly and, where we can’t, point you to
                        a trusted local provider.
                    </p>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="areas-tags">
                        {boroughs.map((b) => (
                            <span key={b} style={{
                                fontSize: 13, padding: "7px 13px",
                                background: "var(--dcc-sage-50)",
                                border: "1px solid var(--dcc-sage-100)",
                                borderRadius: 999, color: "var(--dcc-sage-900)",
                            }}>
                                {b}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="dcc-card" style={{ padding: 34, background: "var(--dcc-sage-900)", color: "var(--dcc-cream)", border: "none" }} data-testid="areas-stats">
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }}>
                        {[
                            { k: "24/7", v: "On-call support & emergency cover" },
                            { k: "3+", v: "Regions covered — London, Essex, SE" },
                            { k: "1 hr", v: "Minimum visits, from as little as" },
                            { k: "48 hrs", v: "Typical care-start once assessed" },
                        ].map((s) => (
                            <div key={s.k} style={{ borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 18 }}>
                                <div className="dcc-serif" style={{ fontSize: 34, color: "var(--dcc-gold-2)", lineHeight: 1 }}>{s.k}</div>
                                <div style={{ fontSize: 13.5, color: "rgba(246,241,231,0.8)", marginTop: 8, lineHeight: 1.5 }}>{s.v}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <style>{`@media (max-width: 900px) {
                #areas .dcc-container { grid-template-columns: 1fr !important; gap: 40px !important; }
            }`}</style>
        </section>
    );
};

/* ---------------- CTA BAND ---------------- */
const CTABand = () => (
    <section data-testid="cta-band" style={{ padding: "70px 0", background: "var(--dcc-gold)", color: "var(--dcc-sage-900)", position: "relative", overflow: "hidden" }}>
        <div className="dcc-container" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 40, flexWrap: "wrap" }}>
            <div>
                <h3 className="dcc-serif" style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: 1.15, marginBottom: 10, letterSpacing: "-0.015em" }}>
                    Request a free, no-obligation care assessment.
                </h3>
                <p style={{ fontSize: 16, color: "rgba(31,42,36,0.78)", maxWidth: 560 }}>
                    A quick conversation with a care advisor — usually within the same day. We’ll listen, ask a few
                    questions, and set out clear next steps. No pressure, no cost.
                </p>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <a href="#contact" className="dcc-btn dcc-btn-primary" data-testid="cta-request-assessment">
                    Request an assessment <ArrowRight size={16} />
                </a>
                <a href={`tel:${PHONE_LANDLINE.replace(/\s/g, "")}`} className="dcc-btn dcc-btn-ghost" data-testid="cta-call"
                    style={{ borderColor: "var(--dcc-sage-900)" }}>
                    <Phone size={15} /> {PHONE_LANDLINE}
                </a>
            </div>
        </div>
    </section>
);

/* ---------------- CONTACT ---------------- */
const Contact = () => {
    const [form, setForm] = useState({
        name: "", email: "", phone: "",
        relationship: "self", situation: "planning", message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null); // { type, msg }

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setStatus(null);
        try {
            await axios.post(`${API}/enquiries`, form);
            setStatus({ type: "ok", msg: "Thank you — a care advisor will be in touch shortly. For urgent care needs please call directly." });
            setForm({ name: "", email: "", phone: "", relationship: "self", situation: "planning", message: "" });
        } catch (err) {
            setStatus({ type: "err", msg: "Something went wrong sending your enquiry. Please call us on " + PHONE_LANDLINE + "." });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="contact" className="dcc-section" style={{ background: "var(--dcc-cream)" }} data-testid="contact-section">
            <div className="dcc-container" style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: 50, alignItems: "start" }}>
                <div className="dcc-card" style={{ padding: 40 }}>
                    <span className="dcc-eyebrow">Get in touch</span>
                    <h2 className="dcc-serif" style={{ fontSize: "clamp(28px, 3.2vw, 40px)", marginTop: 16, marginBottom: 10, letterSpacing: "-0.015em" }}>
                        Tell us a little about the person needing care.
                    </h2>
                    <p style={{ color: "var(--dcc-ink-soft)", fontSize: 15.5, lineHeight: 1.65, marginBottom: 26 }}>
                        We’ll respond within one working day. All enquiries are handled confidentially.
                    </p>

                    <form onSubmit={submit} data-testid="enquiry-form">
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                            <div>
                                <label className="dcc-label" htmlFor="name">Your name</label>
                                <input required id="name" name="name" value={form.name} onChange={onChange} className="dcc-input" placeholder="Jane Smith" data-testid="enquiry-name" />
                            </div>
                            <div>
                                <label className="dcc-label" htmlFor="phone">Phone</label>
                                <input required id="phone" name="phone" value={form.phone} onChange={onChange} className="dcc-input" placeholder="07…" data-testid="enquiry-phone" />
                            </div>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label className="dcc-label" htmlFor="email">Email</label>
                            <input required type="email" id="email" name="email" value={form.email} onChange={onChange} className="dcc-input" placeholder="you@example.com" data-testid="enquiry-email" />
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                            <div>
                                <label className="dcc-label" htmlFor="relationship">Enquiring for</label>
                                <select id="relationship" name="relationship" value={form.relationship} onChange={onChange} className="dcc-select" data-testid="enquiry-relationship">
                                    <option value="self">Myself</option>
                                    <option value="parent">A parent</option>
                                    <option value="spouse">Spouse / partner</option>
                                    <option value="relative">Another relative</option>
                                    <option value="professional">I’m a professional (GP, discharge team)</option>
                                </select>
                            </div>
                            <div>
                                <label className="dcc-label" htmlFor="situation">Situation</label>
                                <select id="situation" name="situation" value={form.situation} onChange={onChange} className="dcc-select" data-testid="enquiry-situation">
                                    <option value="planning">Planning ahead</option>
                                    <option value="soon">Need care in a few weeks</option>
                                    <option value="urgent">Urgent — within days</option>
                                    <option value="discharge">Hospital discharge</option>
                                    <option value="respite">Respite for family carer</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ marginBottom: 20 }}>
                            <label className="dcc-label" htmlFor="message">A little about the care needed</label>
                            <textarea id="message" name="message" value={form.message} onChange={onChange} className="dcc-textarea"
                                placeholder="For example: mum lives alone, has early-stage dementia, needs help with mornings and meals…"
                                data-testid="enquiry-message" />
                        </div>

                        <button type="submit" disabled={submitting} className="dcc-btn dcc-btn-primary" data-testid="enquiry-submit"
                            style={{ opacity: submitting ? 0.7 : 1 }}>
                            {submitting ? "Sending…" : (<>Send enquiry <ArrowRight size={16} /></>)}
                        </button>

                        {status && (
                            <div data-testid="enquiry-status"
                                style={{
                                    marginTop: 18, padding: "12px 16px", borderRadius: 10, fontSize: 14,
                                    background: status.type === "ok" ? "var(--dcc-sage-50)" : "#FBEAE4",
                                    color: status.type === "ok" ? "var(--dcc-sage-900)" : "#8A2E17",
                                    border: `1px solid ${status.type === "ok" ? "var(--dcc-sage-100)" : "rgba(217,105,78,0.35)"}`,
                                }}>
                                {status.msg}
                            </div>
                        )}
                    </form>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                    <div className="dcc-urgent" data-testid="urgent-panel">
                        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.18)", display: "grid", placeItems: "center" }}>
                                <AlertCircle size={20} />
                            </div>
                            <div className="dcc-serif" style={{ fontSize: 22 }}>Urgent care needed?</div>
                        </div>
                        <p style={{ fontSize: 14.5, lineHeight: 1.6, color: "rgba(255,247,240,0.92)", marginBottom: 18 }}>
                            Hospital discharge, sudden decline, or family carer unavailable? Call our care line directly —
                            we can often begin support within 24–48 hours.
                        </p>
                        <a href={`tel:${PHONE_MOBILE.replace(/\s/g, "")}`} className="dcc-btn"
                            data-testid="urgent-call-btn"
                            style={{ background: "#fff", color: "var(--dcc-coral)" }}>
                            <Phone size={15} /> {PHONE_MOBILE}
                        </a>
                    </div>

                    <div className="dcc-card" data-testid="contact-details">
                        <h3 className="dcc-serif" style={{ fontSize: 22, marginBottom: 18 }}>Contact details</h3>
                        <ContactRow icon={Phone} label="Office" value={PHONE_LANDLINE} href={`tel:${PHONE_LANDLINE.replace(/\s/g, "")}`} />
                        <ContactRow icon={Phone} label="Care line" value={PHONE_MOBILE} href={`tel:${PHONE_MOBILE.replace(/\s/g, "")}`} />
                        <ContactRow icon={Mail} label="Email" value={EMAIL} href={`mailto:${EMAIL}`} />
                        <ContactRow icon={MapPin} label="Office" value={ADDRESS} />
                        <ContactRow icon={Clock} label="Hours" value="Mon–Sat · 9:00 – 17:00 · 24/7 care line" last />
                    </div>
                </div>
            </div>

            <style>{`
                @media (max-width: 960px) { #contact .dcc-container { grid-template-columns: 1fr !important; } }
                @media (max-width: 560px) { #contact form > div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; } }
            `}</style>
        </section>
    );
};

const ContactRow = ({ icon: Icon, label, value, href, last }) => (
    <div style={{
        display: "flex", alignItems: "flex-start", gap: 14,
        padding: "12px 0", borderBottom: last ? "none" : "1px solid var(--dcc-line-2)",
    }}>
        <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: "var(--dcc-sage-50)", color: "var(--dcc-sage-800)",
            display: "grid", placeItems: "center", flexShrink: 0,
        }}>
            <Icon size={16} />
        </div>
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--dcc-muted)", marginBottom: 3 }}>{label}</div>
            {href ? (
                <a href={href} style={{ color: "var(--dcc-ink)", textDecoration: "none", fontSize: 14.5, wordBreak: "break-word" }}>{value}</a>
            ) : (
                <div style={{ color: "var(--dcc-ink)", fontSize: 14.5 }}>{value}</div>
            )}
        </div>
    </div>
);

/* ---------------- FOOTER ---------------- */
const Footer = () => (
    <footer data-testid="site-footer" style={{ background: "var(--dcc-sage-900)", color: "var(--dcc-cream)", paddingTop: 70, paddingBottom: 30 }}>
        <div className="dcc-container">
            <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: 40 }}>
                <div>
                    <div style={{
                        display: "inline-block",
                        background: "#FFFFFF",
                        borderRadius: 14,
                        padding: "10px 16px",
                        marginBottom: 18,
                    }}>
                        <img src={LOGO_URL} alt="Divine Care Connections Ltd"
                            style={{ height: 54, width: "auto", display: "block" }} />
                    </div>
                    <p style={{ fontSize: 14, color: "rgba(246,241,231,0.72)", lineHeight: 1.65, maxWidth: 360 }}>
                        CQC-registered home care provider serving London, Essex and the South East. Compassionate,
                        person-centred support in the comfort of your home.
                    </p>
                </div>

                <div>
                    <FooterTitle>Explore</FooterTitle>
                    {NAV.map((n) => <FooterLink key={n.href} href={n.href}>{n.label}</FooterLink>)}
                </div>

                <div>
                    <FooterTitle>Services</FooterTitle>
                    <FooterLink href="#services">Personal Care</FooterLink>
                    <FooterLink href="#services">Dementia Care</FooterLink>
                    <FooterLink href="#services">Companionship</FooterLink>
                    <FooterLink href="#services">Respite Care</FooterLink>
                </div>

                <div>
                    <FooterTitle>Contact</FooterTitle>
                    <FooterLink href={`tel:${PHONE_LANDLINE.replace(/\s/g, "")}`}>{PHONE_LANDLINE}</FooterLink>
                    <FooterLink href={`tel:${PHONE_MOBILE.replace(/\s/g, "")}`}>{PHONE_MOBILE}</FooterLink>
                    <FooterLink href={`mailto:${EMAIL}`}>{EMAIL}</FooterLink>
                    <div style={{ fontSize: 13.5, color: "rgba(246,241,231,0.7)", marginTop: 12, lineHeight: 1.55 }}>{ADDRESS}</div>
                </div>
            </div>

            <div style={{
                marginTop: 46, paddingTop: 22, borderTop: "1px solid rgba(255,255,255,0.12)",
                display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16,
                fontSize: 12.5, color: "rgba(246,241,231,0.6)",
            }}>
                <div>© {new Date().getFullYear()} Divine Care Connections Ltd. All rights reserved.</div>
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                    <a href={CQC_PROFILE_URL} target="_blank" rel="noopener noreferrer"
                        data-testid="footer-cqc-link"
                        style={{ color: "rgba(246,241,231,0.7)", textDecoration: "none" }}
                        onMouseEnter={(e) => e.currentTarget.style.color = "var(--dcc-gold-2)"}
                        onMouseLeave={(e) => e.currentTarget.style.color = "rgba(246,241,231,0.7)"}>
                        CQC Registered · Rated Good ↗
                    </a>
                    <span>ICO Registered</span>
                    <span>Fully Insured</span>
                </div>
            </div>
        </div>
        <style>{`@media (max-width: 900px) {
            [data-testid="site-footer"] .dcc-container > div:first-child { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 560px) {
            [data-testid="site-footer"] .dcc-container > div:first-child { grid-template-columns: 1fr !important; }
        }`}</style>
    </footer>
);

const FooterTitle = ({ children }) => (
    <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--dcc-gold-soft)", marginBottom: 16 }}>
        {children}
    </div>
);
const FooterLink = ({ href, children }) => (
    <a href={href} style={{
        display: "block", color: "rgba(246,241,231,0.82)", textDecoration: "none",
        fontSize: 14, padding: "6px 0", transition: "color 200ms ease",
    }} onMouseEnter={(e) => e.currentTarget.style.color = "var(--dcc-gold-2)"}
        onMouseLeave={(e) => e.currentTarget.style.color = "rgba(246,241,231,0.82)"}>
        {children}
    </a>
);

/* ---------------- APP ---------------- */
function App() {
    return (
        <div className="App" data-testid="app-root">
            <Nav />
            <Hero />
            <TrustBar />
            <About />
            <Services />
            <CQC />
            <WhyUs />
            <Reviews />
            <Areas />
            <CTABand />
            <Contact />
            <Footer />
        </div>
    );
}

export default App;
